'use client';
import { useQuery } from '@tanstack/react-query';

import { API_ENDPOINTS } from '@/constants';

// Instatus summary schema (subset we use).
// Docs: https://status.polymarket.com/public-api
// GET https://status.polymarket.com/v3/summary.json
export type InstatusPageStatus = 'UP' | 'HASISSUES' | 'UNDERMAINTENANCE';

// Incident impact, worst → mildest: MAJOROUTAGE, PARTIALOUTAGE, DEGRADEDPERFORMANCE, OPERATIONAL.
export type InstatusIncidentImpact =
  | 'OPERATIONAL'
  | 'DEGRADEDPERFORMANCE'
  | 'PARTIALOUTAGE'
  | 'MAJOROUTAGE';

export interface InstatusIncident {
  id: string;
  name: string;
  started?: string;
  status: string; // INVESTIGATING | IDENTIFIED | MONITORING | RESOLVED
  impact: string; // InstatusIncidentImpact (kept as string — API is uppercase)
  url?: string;
  updatedAt?: string;
}

export interface InstatusMaintenance {
  id: string;
  name: string;
  start?: string;
  status: string; // NOTSTARTEDYET | INPROGRESS | COMPLETED
  duration?: string;
  url?: string;
  updatedAt?: string;
}

export interface PolymarketStatusSummary {
  page: { name?: string; url?: string; status: string };
  activeIncidents?: InstatusIncident[];
  activeMaintenances?: InstatusMaintenance[];
}

async function fetchPolymarketStatus(): Promise<PolymarketStatusSummary> {
  const res = await fetch(API_ENDPOINTS.POLYMARKET_STATUS_SUMMARY, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Polymarket status API responded ${res.status}`);
  return res.json();
}

/**
 * Polls Polymarket's public status page (Instatus). Used to surface a top banner
 * when the upstream API is degraded/down. `retry: false` + error-aware interval
 * keep it cheap and self-healing (backs off to 60s while erroring, else 30s).
 */
export const usePolymarketStatus = () => {
  return useQuery({
    queryKey: ['polymarket-status'],
    queryFn: fetchPolymarketStatus,
    staleTime: 30 * 1000,
    refetchInterval: (query) => (query.state.error ? 60 * 1000 : 30 * 1000),
    refetchOnWindowFocus: true,
    retry: false,
  });
};
